import { useState } from 'react'
import { Share2, Copy, MessageCircle, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { toast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

interface ShareButtonProps {
  postId: string
  title: string
  content: string
  url?: string
  className?: string
  onShare?: (platform: string) => void
}

export function ShareButton({ 
  postId, 
  title, 
  content, 
  url = window.location.href,
  className,
  onShare 
}: ShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast({
        title: "链接已复制",
        description: "分享链接已复制到剪贴板",
      })
      onShare?.('copy')
    } catch (error) {
      toast({
        title: "复制失败",
        description: "请手动复制链接",
        variant: "destructive"
      })
    }
    setIsOpen(false)
  }

  const handleShareToQQ = () => {
    const shareUrl = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(content.slice(0, 100))}`
    // 安全地打开新窗口
    const newWindow = window.open(shareUrl, '_blank', 'noopener,noreferrer')
    if (!newWindow) {
      console.warn('Popup blocked, falling back to current window')
      window.location.href = shareUrl
    }
    onShare?.('qq')
    setIsOpen(false)
  }

  const handleShareToWeibo = () => {
    const shareUrl = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title + ' ' + content.slice(0, 100))}`
    // 安全地打开新窗口
    const newWindow = window.open(shareUrl, '_blank', 'noopener,noreferrer')
    if (!newWindow) {
      console.warn('Popup blocked, falling back to current window')
      window.location.href = shareUrl
    }
    onShare?.('weibo')
    setIsOpen(false)
  }

  const handleShareToWechat = () => {
    // 微信分享通常需要通过二维码或其他方式
    toast({
      title: "微信分享",
      description: "请复制链接后在微信中分享",
    })
    handleCopyLink()
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "gap-2 text-gray-600 hover:text-blue-500 hover:bg-blue-50",
            className
          )}
        >
          <Share2 className="w-4 h-4" />
          <span>分享</span>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={handleCopyLink}>
          <Copy className="w-4 h-4 mr-2" />
          复制链接
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleShareToWechat}>
          <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
          分享到微信
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleShareToQQ}>
          <div className="w-4 h-4 mr-2 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">Q</span>
          </div>
          分享到QQ
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleShareToWeibo}>
          <div className="w-4 h-4 mr-2 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">微</span>
          </div>
          分享到微博
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={() => {
          window.open(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(content + '\n\n' + url)}`)
          onShare?.('email')
          setIsOpen(false)
        }}>
          <ExternalLink className="w-4 h-4 mr-2" />
          邮件分享
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}